import { ConfigurationCard } from '@/components/deployment/ConfigurationCard'
import {
  User,
  Users,
  Building2,
  Check,
  Shield,
  Zap,
  Sparkles,
} from 'lucide-react'

// Use 'any' to avoid importing node-appwrite on client if problematic, or import type if safe.
// Let's use 'any' for simplicity as requested by User style ("fix it").
interface HeroProps {
  currentUser: any
  deployments?: any[]
}

export function Hero({ currentUser }: HeroProps) {
  return (
    <section className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden pt-20">
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_hsl(8_85%_52%/0.18)_0%,_transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_hsl(25_85%_45%/0.12)_0%,_transparent_50%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-orange-500/30 rounded-full blur-sm animate-float" style={{ animationDelay: '0s', animationDuration: '6s' }} />
        <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 bg-orange-400/20 rounded-full blur-sm animate-float" style={{ animationDelay: '2s', animationDuration: '8s' }} />
        <div className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-orange-600/25 rounded-full blur-sm animate-float" style={{ animationDelay: '4s', animationDuration: '7s' }} />
        <div className="absolute top-2/3 right-1/3 w-2 h-2 bg-orange-500/20 rounded-full blur-sm animate-float" style={{ animationDelay: '1s', animationDuration: '9s' }} />
      </div>

      {/* Enhanced Glow Effects */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-r from-orange-500/8 via-orange-600/4 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse-slow" />
      <div className="absolute right-0 bottom-1/4 w-[500px] h-[500px] bg-gradient-to-l from-orange-400/6 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse-slow" style={{ animationDelay: '1s' }} />

      <div className="relative z-10 container mx-auto max-w-7xl grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
        {/* Left Content */}
        <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
          {/* Enhanced Top Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/20 via-orange-500/15 to-orange-600/10 border border-orange-400/40 text-orange-200 text-xs font-bold mb-12 animate-fade-up backdrop-blur-xl shadow-[0_0_25px_rgba(234,88,12,0.2),0_0_50px_rgba(234,88,12,0.1)] relative overflow-hidden group hover:scale-105 transition-transform duration-300 cursor-default"
            style={{ animationDelay: '50ms' }}
          >
            {/* Animated shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-300/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-400/10 to-orange-500/0 animate-shimmer" />
            <Sparkles className="w-4 h-4 text-orange-300 animate-pulse" />
            <span className="relative z-10 tracking-wider uppercase">
              Powered by OpenClaw
            </span>
          </div>

          {/* Enhanced Main Headline with Gradient Animation */}
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-black mb-8 leading-[1.1] tracking-tight animate-fade-up"
            style={{ animationDelay: '100ms' }}
          >
            <span className="block text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]">Deploy AI</span>
            <span className="block relative">
              <span className="text-gradient-animated bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-shift drop-shadow-[0_0_40px_rgba(234,88,12,0.3)]">
                That Actually
              </span>
            </span>
            <span className="block text-white drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]">Does Things</span>
          </h1>

          {/* Enhanced Subheadline */}
          <p
            className="text-lg md:text-xl text-slate-300 max-w-xl mx-auto lg:mx-0 mb-12 leading-relaxed animate-fade-up font-light"
            style={{ animationDelay: '150ms' }}
          >
            Your personal AI agent for emails, calendar, and tasks.{' '}
            <span className="text-orange-400 font-medium">One-click deployment</span> to any chat app.
          </p>

          {/* Enhanced Use Case Cards with Glassmorphism */}
          <div
            className="grid grid-cols-3 gap-4 w-full max-w-lg mb-12 animate-fade-up"
            style={{ animationDelay: '200ms' }}
          >
            <div className="group flex flex-col items-center gap-3 p-5 rounded-2xl bg-slate-800/40 backdrop-blur-xl border border-slate-700/50 hover:bg-slate-800/60 hover:border-orange-500/50 hover:shadow-[0_0_30px_rgba(234,88,12,0.15)] transition-all duration-500 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/30 to-orange-600/20 border border-orange-500/30 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-[0_0_20px_rgba(234,88,12,0.2)]">
                <User className="w-7 h-7 text-orange-300" />
              </div>
              <span className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors">
                Personal
              </span>
            </div>
            <div className="group flex flex-col items-center gap-3 p-5 rounded-2xl bg-slate-800/40 backdrop-blur-xl border border-slate-700/50 hover:bg-slate-800/60 hover:border-orange-500/50 hover:shadow-[0_0_30px_rgba(234,88,12,0.15)] transition-all duration-500 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/30 to-orange-600/20 border border-orange-500/30 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-[0_0_20px_rgba(234,88,12,0.2)]">
                <Users className="w-7 h-7 text-orange-300" />
              </div>
              <span className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors">
                Community
              </span>
            </div>
            <div className="group flex flex-col items-center gap-3 p-5 rounded-2xl bg-slate-800/40 backdrop-blur-xl border border-slate-700/50 hover:bg-slate-800/60 hover:border-orange-500/50 hover:shadow-[0_0_30px_rgba(234,88,12,0.15)] transition-all duration-500 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/30 to-orange-600/20 border border-orange-500/30 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-[0_0_20px_rgba(234,88,12,0.2)]">
                <Building2 className="w-7 h-7 text-orange-300" />
              </div>
              <span className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors">
                Business
              </span>
            </div>
          </div>

          {/* Enhanced Trust Indicators */}
          <div
            className="flex flex-wrap justify-center lg:justify-start gap-8 animate-fade-up"
            style={{ animationDelay: '250ms' }}
          >
            <div className="flex items-center gap-2.5 text-sm text-slate-300 group hover:text-white transition-colors">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                <Check className="w-3.5 h-3.5 text-green-400" />
              </div>
              <span className="font-medium">One-Click Deploy</span>
            </div>
            <div className="flex items-center gap-2.5 text-sm text-slate-300 group hover:text-white transition-colors">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                <Shield className="w-3.5 h-3.5 text-green-400" />
              </div>
              <span className="font-medium">Isolated VM</span>
            </div>
            <div className="flex items-center gap-2.5 text-sm text-slate-300 group hover:text-white transition-colors">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(34,197,94,0.2)]">
                <Zap className="w-3.5 h-3.5 text-green-400" />
              </div>
              <span className="font-medium">Instant Setup</span>
            </div>
          </div>
        </div>

        {/* Right Side - Enhanced Configuration Card */}
        <div
          className="w-full mx-auto lg:mx-0 animate-fade-up"
          style={{ animationDelay: '300ms' }}
        >
          <div className="relative">
            {/* Glow effect behind card */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-3xl blur-2xl scale-105 opacity-50" />
            <ConfigurationCard currentUser={currentUser} autoResume={true} />
          </div>
        </div>
      </div>
    </section>
  )
}
